import { clamp } from "../../../shared/chartMath";

/**
 * The scale a set of compared values share. Values of both signs share an axis
 * through zero, a loss growing left and a gain right: drawn by size from one
 * edge, a loss would read as a bigger gain. Otherwise all grow from the left
 * edge by size, the largest filling the track.
 *
 * With a `minPct` no bar is narrower than that, so a real value does not
 * vanish beside a far larger one. Zero then keeps that distance from either
 * edge of the track: the narrowest bar fits on its own side of zero, and the
 * side a bar is on is never the price of making it visible.
 */
export function sharedScale(values: readonly number[], minPct = 0) {
  const diverging = values.some((v) => v > 0) && values.some((v) => v < 0);
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  // the value furthest from zero, the first of them on a tie
  const extreme = values.reduce(
    (furthest, v) => (Math.abs(v) > Math.abs(furthest) ? v : furthest),
    0,
  );
  // where zero sits on the track, in %, when the values are drawn around it
  const zeroPct = diverging
    ? clamp((-lo / (hi - lo)) * 100, minPct, 100 - minPct)
    : undefined;
  /** Where a value's bar begins and how wide it is, in % of the track. */
  const placement = (v: number) => {
    const zero = zeroPct ?? 0;
    const reachesLeft = v < 0 && diverging;
    // the track on the value's side of zero, and the value that fills it
    const room = reachesLeft ? zero : 100 - zero;
    const filling = diverging ? (reachesLeft ? -lo : hi) : Math.abs(extreme);
    const pct = Math.max(minPct, (Math.abs(v) / (filling || 1)) * room);
    return { pct, start: reachesLeft ? zero - pct : zero, zeroPct };
  };
  return { diverging, lo, hi, extreme, placement };
}
